import { isEmpty, isEqual, isNil } from "lodash-es";
import { softContains } from "../stringUtil";
import type {
  DynamicValueOption,
  SelectOptions,
  FlattenedSelectOption,
  NestedSelectOption,
  SelectOption,
  DynamicValueId,
} from "../../types/Menu";
import type { TFunction } from "i18next";

export const flattenOptions = <T>(options: SelectOptions<T>): FlattenedSelectOption<T>[] => {
  if (isEmpty(options)) {
    return [];
  }

  const recursiveFlatten = <T>(
    options: (SelectOption<T> | NestedSelectOption<T>)[],
    parentKey: string,
  ): FlattenedSelectOption<T>[] =>
    options.flatMap((option) => {
      if ("value" in option) {
        return [{ ...option, title: parentKey } as FlattenedSelectOption<T>];
      }

      return Object.values(option).flatMap((nestedOptions) => recursiveFlatten(nestedOptions, parentKey));
    });

  return Object.entries(options).flatMap(([key, nestedOptions]) => recursiveFlatten(nestedOptions, key));
};

export const getFilteredOptions = <T>(
  options: (SelectOption<T> | NestedSelectOption<T>)[],
  query: string,
): (SelectOption<T> | NestedSelectOption<T>)[] =>
  options.flatMap((option) => {
    if ("value" in option) {
      if ("pill" in option && !isNil(option.pill)) {
        return softContains(option.label as string, query) || softContains(option.pill as string, query)
          ? [option]
          : [];
      }
      return softContains(option.label as string, query) ? [option] : [];
    }

    const filteredGroup = Object.fromEntries(
      Object.entries(option)
        .map(([key, value]) => [key, getFilteredOptions(value, query)])
        .filter(([, filteredValues]) => !isEmpty(filteredValues)),
    );

    return !isEmpty(Object.keys(filteredGroup)) ? [filteredGroup] : [];
  });

export const getAvailableCategories = <T>(options: SelectOptions<T>): SelectOption<string>[] => {
  if (isNil(options)) {
    return [];
  }
  return Object.keys(options)
    .filter((key) => {
      const categoryOptions = options[key];

      if (isEmpty(categoryOptions)) {
        return false;
      }

      return categoryOptions.some((option) => {
        // If it's a direct option
        if ("value" in option) {
          return true;
        }

        // If it's a nested object, check if any of its nested arrays are non-empty
        return Object.values(option).some((nestedArray) => !isEmpty(nestedArray));
      });
    })
    .map((key) => ({ variant: "text", value: key, label: key }));
};

export const findCategoryByValue = <T>(
  options: SelectOptions<T>,
  selectedValue: SelectOption<T>["value"],
): string | undefined => {
  const matchesValue = (items: (SelectOption<T> | NestedSelectOption<T>)[]): boolean =>
    items.some((item) => {
      if ("value" in item) {
        return isEqual(item.value, selectedValue);
      }
      return Object.values(item).some(matchesValue);
    });

  return Object.keys(options).find((key) => matchesValue(options[key]));
};

export const getRenderedHTMLOptions = (listBoxId: string): HTMLElement[] => {
  const listbox = document.getElementById(listBoxId);
  if (isNil(listbox)) {
    return [];
  }
  return Array.from(listbox.querySelectorAll('[role="option"]')) as HTMLElement[];
};

export const createDynamicValueOptions = (
  t: TFunction,
  options: {
    fields: DynamicValueOption<DynamicValueId>[];
    subforms: Record<string, DynamicValueOption<DynamicValueId>[]>[];
    metaVariables: DynamicValueOption<DynamicValueId>[];
    customVariables: DynamicValueOption<DynamicValueId>[];
  },
) => ({
  [t(($) => $.MENU_CATEGORY_FIELDS)]: options.fields,
  [t(($) => $.MENU_CATEGORY_SUBFORMS)]: options.subforms,
  [t(($) => $.MENU_CATEGORY_VARIABLES)]: [
    {
      [t(($) => $.MENU_SECTION_VARIABLES_CUSTOM)]: options.customVariables,
      [t(($) => $.MENU_SECTION_VARIABLES_META)]: options.metaVariables,
    },
  ],
});
